
// src/hooks/useAuth.ts
import { useEffect, useState, useCallback } from "react";
import { apiUrl } from "../config";

export type AuthUser = {
  nickname: string;
  picture?: string;
  country?: string;
  steamId?: string;
  profileUrl?: string;
  realname?: string;
  avatar?: string;
  avatarMedium?: string;
  avatarFull?: string;
  faceit?: {
    playerId: string;
    nickname?: string;
    avatar?: string;
    country?: string;
    faceitUrl?: string;
    game?: {
      skillLevel?: number;
      elo?: number;
    } | null;
  } | null;
  [k: string]: unknown;
};

type MeResponse =
  | { loggedIn: false }
  | { loggedIn: true; user: AuthUser };

/**
 * useAuth — lightweight session hook
 * - Loads /me on mount
 * - Exposes { loading, user, loggedIn, refresh, logout }
 */
export function useAuth() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);

  const getToken = () => {
    try {
      return localStorage.getItem("steam_token");
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("steam_token");
    if (token) {
      try {
        localStorage.setItem("steam_token", token);
      } catch {}
      params.delete("steam_token");
      const next = params.toString();
      const url = `${window.location.pathname}${next ? `?${next}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", url);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const init: RequestInit = { credentials: "include" };
      if (token) {
        init.headers = { Authorization: `Bearer ${token}` };
      }
      const res = await fetch(apiUrl("/me"), init);
      const data: MeResponse = await res.json();
      if (!data || data.loggedIn === false) {
        if (token) {
          try {
            localStorage.removeItem("steam_token");
          } catch {}
        }
        setUser(null);
      } else {
        setUser(data.user);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(apiUrl("/logout"), { credentials: "include" });
    } finally {
      try {
        localStorage.removeItem("steam_token");
      } catch {}
      setUser(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    loading,
    user,
    loggedIn: !!user,
    refresh,
    logout,
  };
}
